import { useState } from 'react'
import type { NavSection } from '../components/nav/nav-sections'

const STORAGE_KEY = 'alphaedge:navCollapsedSections'

// Below this many visible items, defaultCollapsed is ignored: a
// low-privilege user who only sees a dozen links shouldn't have to open
// sections to find them. Explicit user toggles still apply.
const SPARSE_NAV_THRESHOLD = 12

type CollapseDeltas = Readonly<Record<string, boolean>>

// Only user deltas are stored -- defaults are never pre-seeded, so a
// future change to a section's defaultCollapsed reaches every user who
// hasn't explicitly toggled it.
function readDeltas(): CollapseDeltas {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    const deltas: Record<string, boolean> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') deltas[key] = value
    }
    return deltas
  } catch {
    // localStorage unavailable (private mode, disabled storage, etc.)
    return {}
  }
}

function writeDeltas(deltas: CollapseDeltas): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deltas))
  } catch {
    // Non-persistent collapse state is fine; never throw over storage.
  }
}

export interface NavCollapse {
  readonly isCollapsed: (sectionKey: string) => boolean
  readonly toggle: (sectionKey: string) => void
}

// Section collapse state for the sidebar. Precedence per section:
// 1. active-route override (section owning the current route stays open,
//    unless the user toggled it since the last navigation)
// 2. stored user delta
// 3. defaultCollapsed (ignored while the visible nav is sparse)
export function useNavCollapse(
  sections: readonly NavSection[],
  activeSectionKey: string | null,
  currentPath: string,
): NavCollapse {
  const [deltas, setDeltas] = useState<CollapseDeltas>(readDeltas)
  // Sections the user toggled since the last route change: for these the
  // active-route override is suspended, otherwise collapsing the section
  // you're currently in would visibly bounce back open. Reset on
  // navigation via the state-adjustment-during-render pattern.
  const [overridden, setOverridden] = useState<ReadonlySet<string>>(new Set())
  const [lastPath, setLastPath] = useState(currentPath)
  if (lastPath !== currentPath) {
    setLastPath(currentPath)
    setOverridden(new Set())
  }

  const sparse = sections.reduce((count, section) => count + section.items.length, 0) <= SPARSE_NAV_THRESHOLD

  const baseCollapsed = (sectionKey: string): boolean => {
    if (sectionKey in deltas) return deltas[sectionKey]
    if (sparse) return false
    return sections.find((section) => section.key === sectionKey)?.defaultCollapsed ?? false
  }

  const isCollapsed = (sectionKey: string): boolean => {
    if (sectionKey === activeSectionKey && !overridden.has(sectionKey)) return false
    return baseCollapsed(sectionKey)
  }

  const toggle = (sectionKey: string): void => {
    const next = { ...deltas, [sectionKey]: !isCollapsed(sectionKey) }
    setDeltas(next)
    writeDeltas(next)
    setOverridden((prev) => new Set(prev).add(sectionKey))
  }

  return { isCollapsed, toggle }
}
